import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-6 max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-coral hover:text-coral/80 mb-8">
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
        <p className="text-gray-600 mb-8">Last Updated: December 2025</p>

        <div className="bg-white rounded-2xl p-8 shadow-lg space-y-6">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing or using TrendRush's platform and services, you agree to be bound by these Terms of Service and all applicable laws and regulations of India. If you do not agree with these terms, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Definitions</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>"Platform"</strong> refers to TrendRush's website, mobile application, and related services</li>
              <li><strong>"User"</strong> refers to any person accessing or using our Platform</li>
              <li><strong>"Retailer"</strong> refers to fashion stores partnered with TrendRush</li>
              <li><strong>"Services"</strong> refers to the quick commerce and logistics services provided</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Eligibility</h2>
            <p className="text-gray-700 leading-relaxed">
              You must be at least 18 years of age to use our services. By using TrendRush, you represent and warrant that you have the legal capacity to enter into a binding agreement under Indian law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. User Account</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Registration</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              You may need to create an account to access certain features. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate and current.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Account Security</h3>
            <p className="text-gray-700 leading-relaxed">
              You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Orders and Payments</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Product Listings</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              All products displayed on our Platform are subject to availability. We reserve the right to limit quantities and discontinue products. Prices are subject to change without notice.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Payment</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Payment must be made through authorized payment methods. You agree to pay all charges incurred, including applicable taxes and delivery fees. All transactions are processed securely through third-party payment gateways.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">5.3 Order Confirmation</h3>
            <p className="text-gray-700 leading-relaxed">
              Receipt of an order confirmation does not constitute our acceptance of your order. We reserve the right to refuse or cancel any order for any reason, including product availability, errors in pricing, or suspected fraudulent activity.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Delivery</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We strive to deliver orders quickly through our hyperlocal logistics network. Delivery times are estimates and not guaranteed. We are not liable for delays caused by circumstances beyond our control.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Returns and Refunds</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Our return and refund policy complies with the Consumer Protection Act, 2019. You may return products within the specified time frame if they are defective, damaged, or not as described. Terms and conditions apply. Please contact customer support for assistance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. User Conduct</h2>
            <p className="text-gray-700 leading-relaxed mb-3">You agree not to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Use the Platform for any unlawful purpose</li>
              <li>Impersonate any person or entity</li>
              <li>Interfere with or disrupt the Platform's operation</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Post false, misleading, or defamatory content</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed">
              All content on the Platform, including text, graphics, logos, images, and software, is the property of TrendRush or its licensors and is protected by Indian and international intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by Indian law, TrendRush shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services. Our total liability shall not exceed the amount paid by you for the specific product or service giving rise to the claim.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify and hold harmless TrendRush, its affiliates, and their respective officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses arising from your use of the Platform or violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law and Jurisdiction</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising from these Terms or your use of the Platform shall be subject to the exclusive jurisdiction of the courts in India.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Dispute Resolution</h2>
            <p className="text-gray-700 leading-relaxed">
              In the event of any dispute, we encourage you to contact us first to seek an amicable resolution. If unresolved, disputes may be settled through arbitration in accordance with the Arbitration and Conciliation Act, 1996.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Modifications to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. Changes will be effective upon posting on the Platform. Your continued use of our services after changes are posted constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              We may suspend or terminate your access to the Platform at any time, with or without cause or notice, including for violation of these Terms. Upon termination, your right to use the services will immediately cease.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              For questions about these Terms of Service, please contact:
            </p>
            <div className="bg-gray-50 p-6 rounded-xl">
              <p className="text-gray-900 font-semibold mb-2">TrendRush India</p>
              <p className="text-gray-700">Email: support@trendrushindia.com</p>
              <p className="text-gray-700">Phone: +91 8247034033</p>
              <p className="text-gray-700 mt-2">India</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
